import {
  CategoryAccordion_AbsenceFragment,
  CategoryAccordion_AllowanceFragment,
  CategoryAccordion_CarriedOverFragment,
  CategoryAccordion_EmployeeAbsenceCategoryForYearlySummaryFragment,
} from '../../../../__generated__/graphql';
import { formatPeriodName } from './helpers';
import {
  AbsenceCategorySummary,
  AbsenceInfo,
  AllowanceInfo,
  CarryOver,
} from './types';

export const mapSummary = ({
  currentYearSummary: summary,
}: CategoryAccordion_EmployeeAbsenceCategoryForYearlySummaryFragment): AbsenceCategorySummary => {
  return {
    isUnlimited: summary.isUnlimited,
    hasYearlyBasedAccrualPolicy: summary.hasYearlyBasedAccrualPolicy,
    prevPeriod: {
      name: formatPeriodName(summary.previousPeriod),
      startDate: summary.previousPeriod.startDate,
      endDate: summary.previousPeriod.endDate,
      accrued: summary.previousPeriod.accrued,
      allowances: mapAllowances(summary.previousPeriod.allowances),
      taken: summary.previousPeriod.taken,
      takenAbsences: summary.previousPeriod.takenAbsences.map(mapAbsence),
    },
    curPeriod: {
      name: formatPeriodName(summary.currentPeriod),
      startDate: summary.currentPeriod.startDate,
      endDate: summary.currentPeriod.endDate,
      available: summary.currentPeriod.available,
      accrued: summary.currentPeriod.accrued,
      allowances: mapAllowances(summary.currentPeriod.allowances),
      taken: summary.currentPeriod.taken,
      scheduled: summary.currentPeriod.planned,
      carriedOverFromPrevYear:
        summary.currentPeriod.carriedOverFromPrevYear.map(mapCarryOver),
      takenAbsences: summary.currentPeriod.takenAbsences.map(mapAbsence),
      scheduledAbsences: summary.currentPeriod.plannedAbsences.map(mapAbsence),
    },
    nextPeriod: {
      name: formatPeriodName(summary.nextPeriod),
      startDate: summary.nextPeriod.startDate,
      endDate: summary.nextPeriod.endDate,
      willAccrue: summary.nextPeriod.willAccrue,
      allowances: mapAllowances(summary.nextPeriod.allowances),
      scheduled: summary.nextPeriod.planned,
      upcoming: summary.nextPeriod.willAccrue
        ? summary.nextPeriod.willAccrue - summary.nextPeriod.planned
        : 0,
      scheduledAbsences: summary.nextPeriod.plannedAbsences.map(mapAbsence),
    },
  };
};

const mapCarryOver = (
  carryOver: CategoryAccordion_CarriedOverFragment,
): CarryOver => ({
  amount: carryOver.amount,
  used: carryOver.used,
  lost: carryOver.lost,
  expired: carryOver.expired,
  expireOn: carryOver.expireOn,
});

const mapAllowances = (
  allowances: readonly CategoryAccordion_AllowanceFragment[] | null,
): AllowanceInfo[] | null => {
  if (!allowances) {
    return null;
  }
  return mergeAllowancesByAccrualPolicyId(
    allowances.filter((x) => !!x.accrued).map(mapAllowance),
  );
};

const mergeAllowancesByAccrualPolicyId = (
  src: AllowanceInfo[],
): AllowanceInfo[] => {
  const mergedAccrualPoliciesMap = new Map<string, AllowanceInfo>();
  return src.reduce((acc, cur) => {
    const { accrualPolicyId } = cur;
    if (!accrualPolicyId) {
      acc.push(cur);
      return acc;
    }
    if (!mergedAccrualPoliciesMap.has(accrualPolicyId)) {
      mergedAccrualPoliciesMap.set(accrualPolicyId, cur);
      acc.push(cur);
    } else {
      mergedAccrualPoliciesMap.get(accrualPolicyId)!.amount += cur.amount;
    }
    return acc;
  }, new Array<AllowanceInfo>());
};

const mapAllowance = (
  src: CategoryAccordion_AllowanceFragment,
): AllowanceInfo => ({
  absenceAllowanceId: src.absenceAllowanceId ?? 'system',
  accrualPolicyId: src.accrualPolicyId,
  amount: src.accrued,
  title: src.title,
});

const mapAbsence = (
  absence: CategoryAccordion_AbsenceFragment,
): AbsenceInfo => ({
  absenceId: absence.Id,
  startDay: absence.flair__Start_Date__c,
  endDay: absence.flair__End_Date__c,
  amount: absence.flair__Working_Amount__c,
});
